<template>
  <div class="base-container">
    <FaqPanel page="feeds" :top-offset="50" :max-width="350" :backdrop="true" />
    <MalwareModal
      :id="current.id"
      v-model="malwareWindowIsVisible"
      :name="current.name"
      type="Feed"
    />
    <el-container class="feeds-page">
      <el-main class="shadow">
        <Sticky :z-index="10" :sticky-top="50" class-name="filter-panel">
          <FilterPanel
            v-model="filter"
            :reset-value="defaultFilter"
            @update="update"
          />
        </Sticky>
      </el-main>
    </el-container>
    <el-main class="new-feed-block">
      <router-link to="/feeds/new">
        <el-button
          class="mr-1"
          type="primary"
          size="small"
          icon="PlusElementIcon"
          round
        >
          Add feed
        </el-button>
      </router-link>
    </el-main>
    <LoadingBlock :loading="loading" />
    <div v-if="!loading && items.length > 0">
      <FeedRow v-for="feed in items" :key="feed.id" :feed-id="feed.id" />
      <Pagination
        v-if="total > 0"
        class="mt-1"
        :total="total"
        :page="page"
        @pagination="update"
      />
    </div>
    <div v-if="!loading && items.length < 1" class="empty-result-wrapper">
      <div class="el-alert el-alert--warning is-light empty-result">
        <h4 class="empty-result-text">
          We haven't found any matches for your request, you can
          <el-button
            class="reset-filters-button el-tag el-tag--danger"
            icon="RefreshElementIcon"
            size="small"
            @click="reset"
          >
            reset filters
          </el-button>
        </h4>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, provide } from 'vue'
import { useFeedsStore } from '@/store/modules/feeds'
import FeedRow from './components/FeedRow.vue'
import FilterPanel from './components/FilterPanel.vue'
import LoadingBlock from '@/components/LoadingBlock.vue'
import Pagination from '@/components/Pagination.vue'
import Sticky from '@/components/Sticky.vue'

import { IFeedsPayload } from '@/api/feeds'
import FaqPanel from '@/components/FaqPanel/Index.vue'
import MalwareModal from '@/apps/traforama/components/malware/Modal.vue'

export default defineComponent({
  name: 'FeedsList',
  components: {
    FeedRow,
    LoadingBlock,
    Pagination,
    FilterPanel,
    Sticky,
    FaqPanel,
    MalwareModal
  },
  setup(_props, context) {
    const feedsStore = useFeedsStore()

    const total = computed(() => feedsStore.total)
    const items = computed(() => feedsStore.currentItems)
    const page = computed(() => feedsStore.currentPage)
    const loading = computed(() => feedsStore.listLoading)
    const filter = computed({
      get: () => feedsStore.filter,
      set: (val: IAdScanAlertFilter) => feedsStore.SetFilters(val)
    })
    const defaultFilter = computed(() => feedsStore.defaultFilter)

    const update = ({
      limit = 15,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      feedsStore.SetCurrentPage(newPage)
      feedsStore.FetchItems()
    }

    const reset = () => {
      filter.value = JSON.parse(JSON.stringify(defaultFilter.value))
      update({})
    }

    onMounted(() => update({}))

    const current = ref({})
    const malwareWindowIsVisible = ref(false)
    const openMalwareModal = (item) => {
      current.value = item
      malwareWindowIsVisible.value = true
    }

    provide('openMalwareModal', openMalwareModal)

    return {
      items,
      loading,
      total,
      page,
      update,
      filter,
      defaultFilter,
      reset,
      current,
      malwareWindowIsVisible
    }
  }
})
</script>

<style lang="scss" scoped>
.feeds-page {
  display: flex;
  justify-content: space-between;
}

.el-container {
  margin: 0;
}

:deep(.filter-panel) {
  border-radius: 5px;
  .el-card__body {
    padding: 0px 5px;
  }

  h3 {
    margin: 2px 8px 5px 8px;
    padding-top: 5px;
  }
}

.new-feed-block {
  display: flex;
  justify-content: flex-end;
  padding: 8px 20px;
}

.empty-result-wrapper {
  box-sizing: border-box;
  padding: 0 20px;
  width: 100%;
}

.empty-result {
  margin: 0;
  padding: 30px 20px;
  width: 100%;
}

.empty-result-text {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
}

.reset-filters-button {
  cursor: pointer;
  flex-shrink: 0;
}

:deep(.el-loading-mask) {
  margin: 0px 20px;
}

:deep(.el-card.is-always-shadow) {
  box-shadow: none;
}
</style>
